﻿import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CreditCard, ExternalLink, FileText, LogIn, LogOut, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CheckInEvidence, Stay } from '../../../../../packages/contracts/src/index.js';
import { ActionMenu } from '../../components/ui/action-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { CustomerSearchBox, SearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { CheckInEvidenceDialog, type CheckInTarget } from '../../components/check-in/CheckInEvidenceDialog';
import { LegalDocumentsDialog } from '../../components/check-in/LegalDocumentsDialog';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { TimeRange } from '../../components/ui/time-range';
import { ListDateFilters } from '../../components/ui/list-date-filters';
import { api } from '../../shared/api';
import { dueCentsWithFrozenBill, nightsBetween, paidCentsForSource, paymentStatus } from '../../shared/billing';
import { formatDate } from '../../shared/format';
import { copy, labelForStayStatus } from '../../shared/i18n';
import { dateRangeForPreset, overlapsDateRange, useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';

export function StaysPage() {
  const queryClient = useQueryClient();
  const defaultRange = useMemo(() => dateRangeForPreset('today'), []);
  const { values: filters, setValue, setValues } = useUrlListFilters({ q: '', customerId: '', checkIn: '', range: 'today', from: defaultRange.from, to: defaultRange.to, page: '1', pageSize: '10' }, { keepDefaults: true });
  const search = filters.q;
  const [checkInTarget, setCheckInTarget] = useState<CheckInTarget | null>(null);
  const [documentsTarget, setDocumentsTarget] = useState<Stay | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const stayListOptions = { limit: search ? 100 : Math.min(page * pageSize + 1, 100), from: filters.from, to: filters.to };
  const staysQuery = useQuery({ queryKey: ['stays', stayListOptions], queryFn: () => api.stays.list(stayListOptions) });
  const dogsQuery = useQuery({ queryKey: ['dogs'], queryFn: () => api.dogs.list({ limit: 100 }) });
  const customersQuery = useQuery({ queryKey: ['customers', 'stays'], queryFn: () => api.customers.list({ limit: 100 }) });
  const paymentsQuery = useQuery({ queryKey: ['payments', 'stays'], queryFn: () => api.payments.list({ limit: 100 }) });
  const stays = staysQuery.data?.items ?? [];
  const dogs = dogsQuery.data?.items ?? [];
  const customers = customersQuery.data?.items ?? [];
  const filteredStays = useMemo(
    () => stays.filter((stay) => {
      const dogNames = dogIdsForStay(stay).map((dogId) => dogs.find((item) => item.id === dogId)?.name ?? dogId);
      const customer = customers.find((item) => item.id === stay.customerId);
      if (!overlapsDateRange(stay.checkInDate, stay.checkOutDate, filters.from, filters.to)) return false;
      if (filters.customerId && stay.customerId !== filters.customerId) return false;
      if (filters.checkIn === 'checked-in' && stay.status !== 'checked-in') return false;
      if (filters.checkIn === 'not-checked-in' && stay.status !== 'reserved') return false;
      return matchesSearch(search, [customer?.firstName, customer?.lastName, ...dogNames, stay.shortId, stay.checkInDate, stay.checkOutDate, stay.kennel, stay.room, labelForStayStatus(stay.status)]);
    }),
    [customers, dogs, filters.checkIn, filters.customerId, filters.from, filters.to, search, stays],
  );
  const staysPage = useControlledPagination(filteredStays, page, (nextPage) => setValue('page', String(nextPage)), pageSize);
  const checkIn = useMutation({
    mutationFn: ({ id, evidence }: { readonly id: string; readonly evidence: CheckInEvidence }) => api.stays.checkIn(id, { evidence }),
    onSuccess: async () => {
      setCheckInTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
    },
  });

  const checkOut = useMutation({
    mutationFn: api.stays.checkOut,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['stays'] }),
  });

  const deleteStay = useMutation({
    mutationFn: api.stays.remove,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stays'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
  const confirmDelete = (stayId: string, dogName: string) => setConfirm({
    title: 'Eliminar hotel',
    body: copy.stays.deleteConfirm(dogName),
    confirmLabel: 'Eliminar',
    onConfirm: () => deleteStay.mutate(stayId),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">{copy.stays.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{copy.stays.body}</p>
        </div>
        <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/stays/new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuevo hotel
        </Link>
      </div>

      <section>
        <Card>
          <CardHeader>
            <div className="flex flex-col items-start gap-3">
              <h2 className="text-sm font-semibold">{copy.stays.board}</h2>
              <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Buscar hotel" label="Buscar hotel" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-64">
                <CustomerSearchBox value={filters.customerId} customers={customers} placeholder="Filtrar por cliente" onChange={(customerId) => setValue('customerId', customerId)} />
              </div>
              <SegmentedControl
                value={filters.checkIn as '' | 'checked-in' | 'not-checked-in'}
                options={[{ id: '', label: 'Todos' }, { id: 'checked-in', label: 'Con check-in' }, { id: 'not-checked-in', label: 'Sin check-in' }]}
                onChange={(nextCheckIn) => setValue('checkIn', nextCheckIn)}
                ariaLabel="Filtrar hoteles por check-in"
              />
            </div>
            <ListDateFilters range={filters.range} from={filters.from} to={filters.to} onChange={setValues} startId="staysFrom" endId="staysTo" />
          </CardHeader>
          <CardContent>
            {staysQuery.isLoading || dogsQuery.isLoading || customersQuery.isLoading || paymentsQuery.isLoading ? <LoadingState label={copy.stays.loading} /> : null}
            {staysQuery.isError ? <ErrorState message={staysQuery.error.message} /> : null}
            {dogsQuery.isError ? <ErrorState message={dogsQuery.error.message} /> : null}
            {customersQuery.isError ? <ErrorState message={customersQuery.error.message} /> : null}
            {paymentsQuery.isError ? <ErrorState message={paymentsQuery.error.message} /> : null}
            {staysQuery.data && staysQuery.data.items.length === 0 ? <EmptyState title={copy.stays.emptyTitle} body={copy.stays.emptyBody} /> : null}
            {staysQuery.data && staysQuery.data.items.length > 0 && filteredStays.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro perro, ID de fotos, fecha o estado." /> : null}
            {staysQuery.data && filteredStays.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>{copy.stays.dog}</Th>
                      <Th>Cliente</Th>
                      <Th>Concepto</Th>
                      <Th>ID fotos</Th>
                      <Th>{copy.stays.dates}</Th>
                      <Th>Noches</Th>
                      <Th>{copy.stays.location}</Th>
                      <Th>Check-in</Th>
                      <Th>{copy.common.status}</Th>
                      <Th>Pago</Th>
                      <Th>{copy.common.actions}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {staysPage.pageItems.map((stay) => {
                      const dogNames = dogIdsForStay(stay).map((dogId) => dogs.find((item) => item.id === dogId)?.name ?? dogId);
                      const customer = customers.find((item) => item.id === stay.customerId);
                      const galleryUrl = stay.shortId ? `/gallery/${stay.shortId}` : undefined;
                      const plannedNights = nightsBetween(stay.checkInDate, stay.checkOutDate);
                      const dueCents = dueCentsWithFrozenBill(paymentsQuery.data?.items ?? [], 'stay', stay.id, 0);
                      const paidCents = paidCentsForSource(paymentsQuery.data?.items ?? [], 'stay', stay.id);
                      const payStatus = paymentStatus(dueCents, paidCents);
                      const conceptId = paymentConceptId('stay', stay.id);
                      const checkInState = checkInIndicator(stay);
                      return (
                        <tr key={stay.id}>
                          <Td className="font-medium text-slate-900">{dogNames.join(', ')}</Td>
                          <Td>{customer ? `${customer.firstName} ${customer.lastName}` : stay.customerId}</Td>
                          <Td><span className="font-mono text-xs">{conceptId}</span></Td>
                          <Td><span className="font-mono text-xs">{stay.shortId ?? 'Pendiente'}</span></Td>
                          <Td><TimeRange mode="date" start={stay.checkInDate} end={stay.checkOutDate} /></Td>
                          <Td>
                            <span>{plannedNights} noche(s)</span>
                          </Td>
                          <Td>{stay.kennel ?? stay.room ?? copy.stays.unassigned}</Td>
                          <Td><Badge className={checkInState.className}>{checkInState.label}</Badge></Td>
                          <Td><Badge>{labelForStayStatus(stay.status)}</Badge></Td>
                          <Td><Badge className={paymentToneClass(payStatus.tone)}>{payStatus.label}</Badge></Td>
                          <Td>
                            <ActionMenu groups={[
                              {
                                label: 'Gestion',
                                children: <>
                                  <MenuLink to={`/admin/stays/${stay.id}/edit`} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar hotel</MenuLink>
                                  <MenuLink to={`/admin/payments?customerId=${encodeURIComponent(stay.customerId)}&sourceType=stay&sourceId=${encodeURIComponent(stay.id)}`} icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}>Ir a pagos</MenuLink>
                                </>,
                              },
                              {
                                label: 'Archivos',
                                children: <>
                                  <MenuLink to={`/admin/stays/${stay.id}/photos`} icon={<Camera className="h-4 w-4" aria-hidden="true" />}>Fotos</MenuLink>
                                  <MenuButton onClick={() => setDocumentsTarget(stay)} icon={<FileText className="h-4 w-4" aria-hidden="true" />}>Documentos legales</MenuButton>
                                  <MenuAnchor href={galleryUrl ?? '#'} disabled={!galleryUrl} icon={<ExternalLink className="h-4 w-4" aria-hidden="true" />}>Galeria publica</MenuAnchor>
                                </>,
                              },
                              {
                                label: 'Flujo',
                                children: <>
                                  <MenuButton disabled={stay.status !== 'reserved'} onClick={() => setCheckInTarget({ id: stay.id, kind: 'stay', label: `Hotel - ${dogNames.join(', ')}` })} icon={<LogIn className="h-4 w-4" aria-hidden="true" />}>Registrar entrada</MenuButton>
                                  <MenuButton disabled={stay.status !== 'checked-in'} onClick={() => checkOut.mutate(stay.id)} icon={<LogOut className="h-4 w-4" aria-hidden="true" />}>Confirmar salida</MenuButton>
                                </>,
                              },
                              {
                                label: 'Riesgo',
                                tone: 'danger',
                                children: <MenuButton danger disabled={deleteStay.isPending} onClick={() => confirmDelete(stay.id, dogNames.join(', '))} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}>Eliminar</MenuButton>,
                              },
                            ]} />
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : null}
            <PaginationControls {...staysPage} onPageChange={staysPage.setPage} onPageSizeChange={(nextPageSize) => setValues({ pageSize: String(nextPageSize), page: '1' })} />
            {checkIn.isError ? <p className="mt-3 text-sm text-rose-600">{checkIn.error.message}</p> : null}
            {checkOut.isError ? <p className="mt-3 text-sm text-rose-600">{checkOut.error.message}</p> : null}
            {deleteStay.isError ? <p className="mt-3 text-sm text-rose-600">{deleteStay.error.message}</p> : null}
          </CardContent>
        </Card>
      </section>
      <CheckInEvidenceDialog
        target={checkInTarget}
        onClose={() => setCheckInTarget(null)}
        onSubmit={async (evidence) => { await checkIn.mutateAsync({ id: checkInTarget?.id ?? '', evidence }); }}
      />
      {documentsTarget ? (
        <LegalDocumentsDialog
          title={`Hotel - ${dogIdsForStay(documentsTarget).map((dogId) => dogs.find((dog) => dog.id === dogId)?.name ?? dogId).join(', ')}`}
          documents={documentsTarget.checkInDocuments ?? []}
          onClose={() => setDocumentsTarget(null)}
          onClear={async () => {
            await api.stays.update(documentsTarget.id, { checkInDocuments: [] });
            await queryClient.invalidateQueries({ queryKey: ['stays'] });
          }}
        />
      ) : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function paymentConceptId(sourceType: 'stay' | 'daycare' | 'service', sourceId: string) {
  const prefix = sourceType === 'stay' ? 'EST' : sourceType === 'daycare' ? 'GUA' : 'SER';
  return `${prefix}-${sourceId.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase()}`;
}

function paymentToneClass(tone: 'paid' | 'partial' | 'pending') {
  if (tone === 'paid') return 'bg-[#e5f3e4] text-[#2f6b38]';
  if (tone === 'partial') return 'bg-[#fff3d6] text-[#7a5a16]';
  return 'bg-[#f3e5df] text-[#8a4a3a]';
}

function checkInIndicator(stay: Pick<Stay, 'checkedInAt' | 'status'>) {
  const checkedIn = Boolean(stay.checkedInAt) || stay.status === 'checked-in' || stay.status === 'checked-out';
  return checkedIn
    ? { label: 'Con check-in', className: 'bg-[#e5f3e4] text-[#2f6b38]' }
    : { label: 'Sin check-in', className: 'bg-[#fff3d6] text-[#7a5a16]' };
}

function dogIdsForStay(stay: { dogId: string; dogIds?: readonly string[] }) {
  return stay.dogIds?.length ? stay.dogIds : [stay.dogId];
}

function MenuLink({ to, icon, children }: { readonly to: string; readonly icon: ReactNode; readonly children: ReactNode }) {
  return <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={to}>{icon}{children}</Link>;
}

function MenuAnchor({ href, disabled, icon, children }: { readonly href: string; readonly disabled?: boolean; readonly icon: ReactNode; readonly children: ReactNode }) {
  return <a className={`flex items-center gap-2 rounded-md px-2 py-2 ${disabled ? 'pointer-events-none text-[#9aa392]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} href={href} target={disabled ? undefined : '_blank'} rel="noreferrer">{icon}{children}</a>;
}

function MenuButton({ children, danger, disabled, icon, onClick }: { readonly children: ReactNode; readonly danger?: boolean; readonly disabled?: boolean; readonly icon: ReactNode; readonly onClick: () => void }) {
  return <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} disabled={disabled} onClick={onClick}>{icon}{children}</button>;
}

